import { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  localTime, localEndTime, arrivesNextDay, money, localAmount, derivation,
  PAYMENT, KIND, CHANNEL, telHref, mapsHref, fileSize, dayShort, isoInZone,
  zoneTime, deletePrompt, bookingLine,
} from '../lib/format.js';
import {
  fileUrl, uploadDocumentFiles, deleteDocumentFile,
  logVisit, deleteVisit, uploadVisitPhotos, phoneZone,
} from '../lib/api.js';
import { HistoryLine } from './History.jsx';
import Icon, { KIND_ICON } from './Icon.jsx';

/* ── long notes, folded ──────────────────────────────────────────────────── */
//  An agent writes generously and it is right to: the paragraph explaining that
//  Tugu Negara is the largest free-standing bronze group in the world is worth
//  having. It is not worth six inches of a phone screen on the morning you are
//  trying to see when the next thing starts. Two lines and a word, until asked.
//
//  A card decides for itself, but the screen can override every card at once —
//  which is what "Apri tutto" does, and why the default lives in a context
//  rather than in each card's own state.
//
//  And when a card is folded it folds properly: the note goes to two lines and
//  everything that is a way of ACTING on the entry — call, map, mark it done,
//  photograph it, move it — goes with it. A card you have already finished with
//  should be three lines and a word, not three lines and a toolbar.

export const NotesOpen = createContext(false);

/* ── blocks ──────────────────────────────────────────────────────────────── */

// Text as an agent actually writes it: paragraphs separated by blank lines,
// bullets on lines that open with a dash. Rendered as one string — which is what
// {text} does — a 3,500-character glossary of Indonesian dining came out as a
// single grey slab eleven hundred pixels tall, with every list item run into the
// sentence before it. It was in the app and nobody could have read it.
export function noteParts(text) {
  const out = [];
  let para = [];
  let list = null;
  const flushPara = () => { if (para.length) { out.push({ kind: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list?.length) { out.push({ kind: 'ul', items: list }); list = null; } };

  for (const raw of String(text ?? '').split('\n')) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    const bullet = line.match(/^[-–—•*]\s+(.*)$/);
    if (bullet) { flushPara(); (list ??= []).push(bullet[1]); continue; }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return out;
}

// "Warung - the broadest everyday category" is a term and its definition, and
// setting the term apart is the difference between a glossary and a paragraph
// with dashes in it.
const TERM = /^(.{2,44}?)\s+[-–—]\s+(.+)$/;

function NoteBody({ text, foldOver = 260 }) {
  const [open, setOpen] = useState(false);
  const parts = noteParts(text);
  if (parts.length === 0) return null;

  const long = String(text).length > foldOver;
  const shown = long && !open ? parts.slice(0, 1) : parts;

  return (
    <>
      <div className="notebody">
        {shown.map((b, i) => (b.kind === 'p'
          ? <p key={i} className={long && !open ? 'clamp2' : undefined}>{b.text}</p>
          : (
            <ul key={i}>
              {b.items.map((it, j) => {
                const m = it.match(TERM);
                return <li key={j}>{m ? <><b>{m[1]}</b> — {m[2]}</> : it}</li>;
              })}
            </ul>
          )))}
      </div>
      {long && (
        <button className="histline" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <Icon name="chevronRight" size={12} style={{ rotate: open ? '90deg' : '0deg' }} />
          {open ? 'Meno' : 'Altro'}
        </button>
      )}
    </>
  );
}

const KNOWN = new Set(['note', 'warning', 'table', 'checklist', 'links', 'menu',
  'hours', 'gallery', 'tips', 'budget']);

export function Block({ block }) {
  const c = block.content ?? {};

  if (block.type === 'warning') {
    const text = c.text ?? Object.values(c).filter((v) => typeof v === 'string').join(' · ');
    return (
      <div className="block warning">
        <span className="ico"><Icon name="alert" size={16} /></span>
        <div>
          {block.title && <b>{block.title}</b>}
          {/* A warning is short and it is the point of the card, so it never
              folds — but it still gets its line breaks back. */}
          <NoteBody text={text} foldOver={Infinity} />
          {Array.isArray(c.charges) && (
            <p style={{ marginTop: 2 }}>
              {c.charges.map((x) => `${x.provider}: ${x.date}`).join(' · ')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (block.type === 'note') {
    const text = c.text ?? Object.values(c).filter((v) => typeof v === 'string').join(' · ');
    return (
      <div className="block">
        {block.title && <b>{block.title}</b>}
        <NoteBody text={text} />
      </div>
    );
  }

  if (block.type === 'table' && Array.isArray(c.rows)) {
    return (
      <div className="block">
        {block.title && <b>{block.title}</b>}
        <table><tbody>
          {c.rows.map((r, i) => (
            <tr key={i}>{r.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
          ))}
        </tbody></table>
      </div>
    );
  }

  if (block.type === 'links' && Array.isArray(c.items)) {
    return (
      <div className="block">
        {block.title && <b>{block.title}</b>}
        <div className="actions">
          {c.items.map((l, i) => (
            <a className="act" key={i} href={l.url} target="_blank" rel="noreferrer">{l.label}</a>
          ))}
        </div>
      </div>
    );
  }

  // An unrecognised type renders as a labelled key/value card rather than being
  // dropped — the promise that lets an agent invent a block type when nothing
  // existing fits. The data survives even when the styling does not.
  return (
    <div className="block unknown">
      <b>{block.title ?? block.type}</b>
      {!KNOWN.has(block.type) && <span className="tag-unknown">{block.type}</span>}
      <dl>
        {Object.entries(c).map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{k}</dt>
            <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* ── documents ───────────────────────────────────────────────────────────── */

export function DocRow({ doc, onChanged }) {
  const input = useRef(null);
  const camera = useRef(null);
  const [busy, setBusy] = useState(null);      // {index,total,name} while uploading
  const [open, setOpen] = useState(false);
  const files = doc.files ?? [];
  const empty = files.length === 0;

  const openFile = async (f) => {
    try {
      const url = await fileUrl(f);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { alert(e.message); }
  };

  const pick = async (e) => {
    const chosen = e.target.files;
    if (!chosen?.length) return;
    try {
      await uploadDocumentFiles(doc, chosen, setBusy);
      onChanged?.();
    } catch (err) {
      alert(`Caricamento non riuscito: ${err.message}`);
    } finally {
      setBusy(null);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async (f) => {
    if (!confirm(`Eliminare ${f.label || f.file_name}?`)) return;
    try { await deleteDocumentFile(f); onChanged?.(); }
    catch (e) { alert(e.message); }
  };

  const total = files.reduce((n, f) => n + Number(f.size_bytes ?? 0), 0);

  return (
    <>
      <div className="docline">
        <button className={`docrow${empty ? ' expected' : ''}`} disabled={!!busy}
                onClick={() => {
                  if (empty) input.current?.click();
                  else if (files.length === 1) openFile(files[0]);
                  else setOpen((v) => !v);
                }}>
          <Icon name={empty ? 'plus' : 'clip'} size={15} />
          <span className="n">{doc.title}</span>
          <span className="sz">
            {busy ? `${busy.index + 1}/${busy.total}…`
              : empty ? 'carica'
              : files.length > 1 ? `${files.length} file · ${fileSize(total)}`
              : fileSize(total)}
          </span>
          {files.length > 1 && (
            <Icon name={open ? 'chevronLeft' : 'chevronRight'} size={14}
                  style={{ rotate: open ? '90deg' : '0deg', color: 'var(--faint)' }} />
          )}
        </button>
        {/* Straight to the rear camera, for photographing a paper receipt on the
            spot. A separate input because one cannot be both a camera and a
            file browser. */}
        <button className="shoot" disabled={!!busy} aria-label={`Scatta una foto per ${doc.title}`}
                onClick={() => camera.current?.click()}>
          <Icon name="camera" size={15} />
        </button>
      </div>

      {open && files.length > 1 && (
        <div className="filelist">
          {files.map((f) => (
            <div className="filerow" key={f.id}>
              <button className="open" onClick={() => openFile(f)}>
                <Icon name="clip" size={13} />
                <span className="n">{f.label || f.traveler?.name || f.file_name}</span>
                <span className="sz">{fileSize(f.size_bytes)}</span>
              </button>
              <button className="del" onClick={() => remove(f)} aria-label="Elimina">×</button>
            </div>
          ))}
          <button className="addmore" onClick={() => input.current?.click()}>
            <Icon name="plus" size={13} /> Aggiungi un altro file
          </button>
        </div>
      )}

      {/* One slot routinely holds several files, so the picker is multiple by
          default. accept lets iOS offer camera, library and Files together. */}
      <input ref={input} className="hidden-input" type="file" multiple
             accept="application/pdf,image/*" onChange={pick} />
      {/* capture="environment" goes straight to the rear camera. Kept separate
          because a slot may also want files, and one input cannot be both. */}
      <input ref={camera} className="hidden-input" type="file"
             accept="image/*" capture="environment" onChange={pick} />
    </>
  );
}

/* ── done, and the proof ─────────────────────────────────────────────────── */

// Only things a person can be said to have done. A flight you were sitting on
// is not news, and "we did the hotel" means nothing — offering the button there
// would make the diary a chore rather than a record.
const LOGGABLE = new Set(['activity', 'meal']);

// The kinds whose glyph carries information the title does not: you sleep here,
// this one flies. An activity's star said only "this is an activity", over a
// title that had just said what it was.
const ICON_KINDS = new Set(['transport', 'lodging']);

// One tap, standing outside the thing. The time is now and nothing has to be
// filled in, because anything to fill in means it gets logged later or not at
// all. The note and the photo can follow in the diary, or never.
function Done({ event, onChanged }) {
  const camera = useRef(null);
  const [busy, setBusy] = useState(false);
  const v = event.visit;
  const shots = v?.photos?.length ?? 0;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  // A planned thing is recorded at the time it was planned for, NOT at the
  // moment the tick was pressed. Those are the same thing when you tap on the
  // way out and wildly different when you catch up over dinner — «Central
  // Market, 13:30» carrying «Fatto · 16:49» is just the app admitting when you
  // got round to telling it, which is nobody's business and helps nobody.
  //
  // The zone follows the timestamp for the same reason: it has to print the
  // clock the time was taken from, or the card and its badge disagree.
  const planned = event.starts_at
    ?? (event.event_date ? isoInZone(event.event_date, zoneTime(Date.now(), phoneZone()), phoneZone()) : null);

  const mark = () => logVisit({
    trip_id: event.trip_id, title: event.title, event_id: event.id,
    place_id: event.place_id ?? null, stop_id: event.stop_id ?? null,
    visited_at: planned,
    timezone: event.starts_at ? (event.timezone ?? phoneZone()) : phoneZone(),
  });

  const toggle = () => run(async () => {
    if (!v) return void await mark();
    if (shots > 0 && !confirm(
      `Togliere «${event.title}» dal diario? Anche ${shots === 1 ? 'la foto' : `le ${shots} foto`}.`)) return;
    await deleteVisit(v);
  });

  // Photographing something is itself the statement that you were there, so it
  // logs the visit too rather than refusing until you have pressed the tick.
  const shoot = (ev) => {
    const files = ev.target.files;
    if (!files?.length) return;
    run(async () => {
      const visit = v ?? await mark();
      await uploadVisitPhotos({ ...visit, photos: v?.photos ?? [] }, files);
    }).finally(() => { if (camera.current) camera.current.value = ''; });
  };

  // A tick, and only a tick. The word "Fatto" appeared three times on one screen
  // in three different meanings — a button to press, a state already reached, and
  // a heading on a diary entry — which is two too many for a mark that either is
  // or is not there. It sits beside the title where a checkbox belongs, and it
  // stays visible when the card folds, because whether we have done a thing is
  // the one fact about it worth seeing without opening anything.
  return (
    <>
      <button className={`tick${v ? ' on' : ''}`} disabled={busy} onClick={toggle}
              aria-pressed={Boolean(v)}
              aria-label={v ? `${event.title}: fatto. Togli dal diario.`
                            : `Segna ${event.title} come fatto`}>
        <Icon name="check" size={17} />
      </button>
      <input ref={camera} className="hidden-input" type="file"
             accept="image/*" capture="environment" onChange={shoot} />
    </>
  );
}

// The camera lives with the other things you can do to a card, behind the fold.
// Photographing something is itself the statement that you were there, so it
// logs the visit too rather than refusing until the tick has been pressed.
function VisitPhoto({ event, onChanged }) {
  const camera = useRef(null);
  const [busy, setBusy] = useState(false);
  const v = event.visit;
  const shots = v?.photos?.length ?? 0;

  const shoot = (ev) => {
    const files = ev.target.files;
    if (!files?.length) return;
    setBusy(true);
    (async () => {
      try {
        const visit = v ?? await logVisit({
          trip_id: event.trip_id, title: event.title, event_id: event.id,
          place_id: event.place_id ?? null, stop_id: event.stop_id ?? null,
          visited_at: event.starts_at ?? null,
          timezone: event.starts_at ? (event.timezone ?? phoneZone()) : phoneZone(),
        });
        await uploadVisitPhotos({ ...visit, photos: v?.photos ?? [] }, files);
        await onChanged?.();
      } catch (e) { alert(e.message); }
      finally {
        setBusy(false);
        if (camera.current) camera.current.value = '';
      }
    })();
  };

  return (
    <>
      <button className="act" disabled={busy} onClick={() => camera.current?.click()}
              aria-label={`Foto di ${event.title}`}>
        <Icon name="camera" size={14} /> Foto{shots > 0 ? ` · ${shots}` : ''}
      </button>
      <input ref={camera} className="hidden-input" type="file"
             accept="image/*" capture="environment" onChange={shoot} />
    </>
  );
}

/* ── something that happened, with no plan behind it ─────────────────────── */

// The palace you walked past and went into. It was never an event, so there is
// nothing here to move, book or decide — only the record that it happened, at
// the hour it happened, in its place on the day. Editing lives in the diary,
// which is the one screen whose subject is the record itself.
export function VisitCard({ visit: v, time, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const shots = v.photos?.length ?? 0;
  const maps = mapsHref(v.place);
  const more = Boolean((v.note && v.note.length > 46) || maps || onChanged);

  // The same tick as everywhere else, already on, and pressing it means the same
  // thing it means on any other card: this did not happen after all.
  const undo = async () => {
    if (!confirm(`Togliere «${v.title}» dal diario?`)) return;
    setBusy(true);
    try { await deleteVisit(v); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <article className="ev k-activity">
      {time && <div className="ev-top"><span className="ev-time">{time}</span></div>}

      <div className="ev-title">
        <h3>{v.title}</h3>
        {shots > 0 && (
          <span className="state muted"><Icon name="camera" size={12} /> {shots}</span>
        )}
        <button className="tick on" disabled={busy} aria-pressed="true"
                aria-label={`${v.title}: fatto. Togli dal diario.`} onClick={undo}>
          <Icon name="check" size={17} />
        </button>
      </div>

      {v.note && <p className={`det${open ? '' : ' clamp1'}`}>{v.note}</p>}

      {open && (maps || onChanged) && (
        <div className="actions">
          {maps && (
            <a className="act" href={maps} target="_blank" rel="noreferrer">
              <Icon name="pin" size={14} />{v.place?.name ?? 'Mappa'}
            </a>
          )}
          <button className="act danger" disabled={busy} onClick={undo}>
            <Icon name="trash" size={14} /> Togli
          </button>
        </div>
      )}

      {more && (
        <button className="histline" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
          <Icon name="chevronRight" size={12} style={{ rotate: open ? '90deg' : '0deg' }} />
          {open ? 'Meno' : 'Altro'}
        </button>
      )}
    </article>
  );
}

/* ── event ───────────────────────────────────────────────────────────────── */

// hideTime is for the day timeline, where the clock is already printed in the
// rail beside the card and repeating it inside reads as two different times.
// dayHint says that a card belongs to a journey that began, or carries on, on
// another day — the honest thing to say when one booking spans two dates.
export default function EventCard({ event, legs = [], onDocChanged, showDate,
                                   hideTime, dayHint, fromList, onReopen,
                                   moveDays, moveZone, onMove, onDelete }) {
  const [reopening, setReopening] = useState(false);
  const [moving, setMoving] = useState(false);
  const allNotesOpen = useContext(NotesOpen);
  const [mine, setMine] = useState(null);
  // A screen-wide open or close resets the cards that disagreed with it,
  // otherwise "Chiudi tutto" leaves whatever you opened by hand still open.
  useEffect(() => { setMine(null); }, [allNotesOpen]);
  const e = event;
  const d = e.details ?? {};
  const pay = e.payment_status ? PAYMENT[e.payment_status] : null;
  const time = localTime(e);
  const end = localEndTime(e);
  const showTime = time && !e.all_day && !hideTime;

  // Every card folds, not just the verbose ones. Folding only the long entries
  // gave a day where some cards carried a row of buttons and some did not, for
  // a reason — the length of a note — that has nothing to do with anything the
  // traveller is looking for. Same shape for all of them: a tick, a title, one
  // line, and a way in.
  const open = mine ?? allNotesOpen;
  const folded = !open;

  // The provider is only worth printing when it says something the title does
  // not — "Eq Kuala Lumpur · Eq Kuala Lumpur" was the first draft's giveaway.
  const providerIsNews = e.provider
    && !e.title.toLowerCase().includes(e.provider.toLowerCase());

  // With the clock in the timeline gutter the top line is free, and a flight
  // number is what you actually scan a departure board for. It then comes out of
  // the detail line, where it would otherwise appear twice.
  //
  // What it is NOT for is the word "Attività" over an entry whose title already
  // says what it is. That line existed to hold a time, and where there is no
  // time it was padding itself with the one thing on the card nobody needed.
  const topLabel = !showTime && d.number ? d.number : null;

  const facts = [];
  if (providerIsNews) facts.push(e.provider);
  if (d.number && topLabel !== d.number) facts.push(d.number);
  if (d.aircraft) facts.push(d.aircraft);
  if (d.duration) facts.push(d.duration);
  if (end && e.kind === 'transport')
    facts.push(`arrivo ${end}${arrivesNextDay(e) ? '+1' : ''}`);
  if (d.terminal) facts.push(d.terminal);
  if (d.room) facts.push(d.room);
  if (d.nights) facts.push(`${d.nights} notti`);
  if (d.board) facts.push(d.board);

  const meta = [];
  if (e.confirmation) meta.push(e.confirmation);
  // "ritrovo Concourse Level" without the hour was half an instruction. The
  // hour was in the row the whole time.
  if (e.meeting_point || e.meeting_time) {
    meta.push(['ritrovo', e.meeting_time, e.meeting_point].filter(Boolean).join(' '));
  }
  if (e.booking_channel) meta.push(CHANNEL[e.booking_channel] ?? e.booking_channel);
  if (d.baggage) meta.push(d.baggage);
  if (e.place?.price_level) meta.push('€'.repeat(Math.min(4, e.place.price_level)));

  // Who is on it and where they sit. Seat and ticket number differ per person
  // on the same booking, which is exactly why they were unreadable anywhere.
  const seats = (e.travelers ?? []).filter((t) => t.seat || t.ticket_number);

  // Open hours arrive as freeform JSON, so they are printed as given rather
  // than parsed into a promise the data cannot keep.
  const hours = e.place?.open_hours && typeof e.place.open_hours === 'object'
    ? Object.entries(e.place.open_hours)
      .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
      .map(([k, v]) => `${k} ${v}`)
    : [];

  const phone = e.vendor?.whatsapp || e.vendor?.phone || e.place?.phone;
  const maps = mapsHref(e.place);
  const deal = e.unit_list_amount && e.unit_amount
    && Number(e.unit_list_amount) > Number(e.unit_amount);
  const derived = derivation(e);

  // One state only. Two pills side by side was noise, and "needs deciding"
  // always outranks "how it is paid".
  // "da verificare" against an activity with no price tells you nothing, so a
  // payment state only shows where there is money involved.
  const hasMoney = e.total_home != null || e.total_local != null;
  const primaryState = e.decided === false
    ? <span className="badge-solid">da decidere</span>
    // The contract has promised since day one that low-confidence entries are
    // marked until confirmed. Nothing marked them, so an agent's guess at a
    // time read exactly like a time off a boarding pass.
    : e.confidence === 'low'
      ? <span className="state attn">da confermare</span>
      : (pay && hasMoney && e.payment_status !== 'unknown')
        ? <span className={`state ${pay.tone}`}>{pay.label}</span>
        : null;

  // What a folded card says: the note if there is one, otherwise the facts.
  // Enough to recognise the entry, never enough to bury the next one.
  const oneLine = e.notes || (facts.length > 0 ? facts.join(' · ') : null);

  // Whether opening it would show anything at all. A short note with no place,
  // no booking and nothing to do has nothing behind it, and an «Altro» that
  // opens onto nothing is worse than no «Altro».
  const more = Boolean(
    (e.notes && e.notes.length > 46) || (e.notes && facts.length > 0)
    || deal || e.inclusions?.length || e.exclusions?.length || legs.length
    || meta.length || derived || e.place?.history || phone || maps
    || LOGGABLE.has(e.kind) || e.blocks?.length || e.documents?.length
    || onReopen || onMove || onDelete);

  // The top line earns its place or it does not appear: a clock, a flight
  // number, a date that is not this one, a state that needs deciding.
  const topRow = Boolean(showTime || (!showTime && e.time_note) || topLabel
    || (showDate && e.event_date) || dayHint || primaryState);

  return (
    <article className={`ev k-${e.kind}${e.decided === false ? ' unresolved' : ''}`}>
      {topRow && (
        <div className="ev-top">
          {/* A plane and a bed say something a title often does not. A star over
              the word "Attività" said nothing twice. */}
          {ICON_KINDS.has(e.kind) && (
            <span className="ev-kicon"><Icon name={KIND_ICON[e.kind] ?? 'note'} size={14} /></span>
          )}
          {showTime ? <span className="ev-time">{time}</span> : null}
          {!showTime && e.time_note ? <span>{e.time_note}</span> : null}
          {!showTime && topLabel ? <span className="ev-ref">{topLabel}</span> : null}
          {showDate && e.event_date && <span>{showDate}</span>}
          {dayHint && <span className="ev-hint">{dayHint}</span>}
          <span style={{ marginLeft: 'auto' }}>{primaryState}</span>
        </div>
      )}

      <div className="ev-title">
        <h3>{e.title}</h3>
        {e.total_home != null && <span className="money">{money(e.total_home)}</span>}
        {LOGGABLE.has(e.kind) && <Done event={e} onChanged={onDocChanged} />}
      </div>

      {folded
        ? (oneLine && <p className="det clamp1">{oneLine}</p>)
        : (
          <>
            {facts.length > 0 && <p className="det">{facts.join(' · ')}</p>}
            {e.notes && <p className="det">{e.notes}</p>}

            {deal && (
              <p className="det">
                Concordato <b>{localAmount(e.unit_amount, e.currency_local)}</b>{' '}
                <span style={{ textDecoration: 'line-through', color: 'var(--faint)' }}>
                  {localAmount(e.unit_list_amount, e.currency_local)}
                </span>
                {e.payment_terms ? ` · ${e.payment_terms}` : ''}
              </p>
            )}

            {e.inclusions?.length > 0 && (
              <p className="sub"><span style={{ color: 'var(--ok)', fontWeight: 600 }}>Incluso</span>{' '}
                {e.inclusions.join(' · ')}</p>
            )}
            {e.exclusions?.length > 0 && (
              <p className="sub"><span style={{ color: 'var(--attn)', fontWeight: 600 }}>Escluso</span>{' '}
                {e.exclusions.join(' · ')}</p>
            )}

            {legs.length > 0 && (
              <div className="legs">
                {legs.map((l) => (
                  <div className="leg" key={l.id}>
                    <span className="t">
                      {localTime(l)}
                      {localEndTime(l) ? `–${localEndTime(l)}${arrivesNextDay(l) ? '+1' : ''}` : ''}
                    </span>
                    <span className="n">
                      {l.title}
                      <small>{[l.details?.number, l.details?.aircraft,
                        localEndTime(l) ? null : l.details?.duration, l.details?.baggage]
                        .filter(Boolean).join(' · ')}</small>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {(meta.length > 0 || derived) && (
              <p className="sub">
                {meta.join(' · ')}
                {derived && meta.length > 0 ? ' · ' : ''}
                {derived && <span className="derive">{derived}</span>}
              </p>
            )}

            {seats.length > 0 && (
              <p className="sub">
                {seats.map((t) => [t.name, t.seat, t.ticket_number].filter(Boolean).join(' '))
                  .join(' · ')}
              </p>
            )}

            {hours.length > 0 && <p className="sub">{hours.join(' · ')}</p>}
            {bookingLine(e.place) && <p className="sub">{bookingLine(e.place)}</p>}
            {e.place?.notes && <p className="sub">{e.place.notes}</p>}
            {e.vendor?.languages?.length > 0 && (
              <p className="sub">parla {e.vendor.languages.join(', ')}</p>
            )}
            {e.vendor?.notes && <p className="sub">{e.vendor.notes}</p>}
            {e.place?.tags?.length > 0 && <p className="tags">{e.place.tags.join(' · ')}</p>}

            {/* An arrangement with no voucher IS its wording. This was written
                on every WhatsApp deal in the trip and shown on none of them. */}
            {e.agreed_terms && (
              <p className="sub"><span style={{ fontWeight: 600 }}>Accordo</span>{' '}
                {e.agreed_terms}</p>
            )}
            {e.payment_note && <p className="sub">{e.payment_note}</p>}
            {e.cancellation_policy && (
              <p className="sub"><span style={{ fontWeight: 600 }}>Cancellazione</span>{' '}
                {e.cancellation_policy}</p>
            )}

            {/* The story of the place survives being decided on. Choosing an
                option turns it into this card, and without this the history you
                read while choosing would vanish the moment you committed. */}
            <HistoryLine text={e.place?.history} />

            {(phone || maps || LOGGABLE.has(e.kind)) && (
              <div className="actions">
                {phone && (
                  <a className="act" href={telHref(phone)}>
                    <Icon name="phone" size={14} />Chiama
                  </a>
                )}
                {maps && (
                  <a className="act" href={maps} target="_blank" rel="noreferrer">
                    <Icon name="pin" size={14} />{e.place?.name ?? 'Mappa'}
                  </a>
                )}
                {e.place?.booking_url && (
                  <a className="act" href={e.place.booking_url} target="_blank" rel="noreferrer">
                    Prenota
                  </a>
                )}
                {e.place?.website && !e.place?.booking_url && (
                  <a className="act" href={e.place.website} target="_blank" rel="noreferrer">
                    Sito
                  </a>
                )}
                {LOGGABLE.has(e.kind) && <VisitPhoto event={e} onChanged={onDocChanged} />}
                {/* The swipe is the quick way; this is the one that works with a
                    keyboard, a mouse, and a thumb that never found the gesture. */}
                {onDelete && (
                  <button className="act danger"
                          onClick={() => { if (confirm(deletePrompt(e, legs))) onDelete(); }}>
                    <Icon name="trash" size={14} /> Elimina
                  </button>
                )}
              </div>
            )}

            {e.blocks?.map((b) => <Block key={b.id} block={b} />)}

            {/* Where this came from. An agent is asked for it on every write and
                nothing has ever shown it, which is the difference between a trip
                you can audit and one you have to trust. */}
            {e.source && <p className="from">da {e.source}</p>}

            {/* Behind the fold with everything else. A ticket is not decoration,
                but on the day you need it the band at the top of Oggi is already
                holding it out to you, and everywhere else it is one tap from
                here and one tab away in Documenti. */}
            {e.documents?.length > 0 && (
              <div className="docs-inline">
                {e.documents.map((doc) => (
                  <DocRow key={doc.id} doc={doc} onChanged={onDocChanged} />
                ))}
              </div>
            )}
          </>
        )}

      {more && (
        <button className="histline" aria-expanded={!folded} onClick={() => setMine(folded)}>
          <Icon name="chevronRight" size={12}
                style={{ rotate: folded ? '0deg' : '90deg' }} />
          {folded ? 'Altro' : 'Meno'}
        </button>
      )}

      {/* This came from a choice, so the way to change your mind lives here —
          on the card you can actually see — rather than on a duplicate.
          Two different second thoughts, and they cost different things: moving
          the day keeps the booking, reopening the decision throws it away. */}
      {!folded && (onReopen || onMove) && (
        <div className="chosenfrom">
          {/* Not everything movable came from a shortlist, and calling a flight
              you booked yourself "scelto" is a small lie. */}
          <span>{fromList ? `scelto da «${fromList}»` : onReopen ? 'scelto' : ''}</span>
          {onMove && moveDays?.length > 0 && (
            <button className="act" aria-expanded={moving} onClick={() => setMoving((v) => !v)}>
              <Icon name="today" size={13} /> Sposta
            </button>
          )}
          {onReopen && (
            <button className="act quiet" disabled={reopening}
                    onClick={async () => {
                      setReopening(true);
                      try { await onReopen(); } finally { setReopening(false); }
                    }}>
              {reopening ? 'Attendi…' : 'Cambia'}
            </button>
          )}
        </div>
      )}

      {moving && <MoveDay event={e} days={moveDays} zone={moveZone}
                          onDone={() => setMoving(false)} onMove={onMove} />}
    </article>
  );
}

// Changing the day of something already decided. The same day chips as choosing
// an option, because it is the same question — but the time starts filled in
// with whatever was already agreed, since a dinner moved to Thursday is still at
// 19:30 until someone says otherwise.
function MoveDay({ event, days, zone, onMove, onDone }) {
  const [day, setDay] = useState(event.event_date ?? '');
  const [time, setTime] = useState(localTime(event) ?? '');
  const [busy, setBusy] = useState(false);
  const eventZone = event.timezone ?? zone ?? null;
  const unchanged = day === (event.event_date ?? '') && time === (localTime(event) ?? '');

  return (
    <div className="pickday">
      <p className="q">Spostare a quale giorno?</p>
      <div className="days">
        {days.map((d) => (
          <button key={d} className={`daychip${day === d ? ' on' : ''}`}
                  onClick={() => setDay(d)}>
            {dayShort(d)}
          </button>
        ))}
        <button className={`daychip${day === '' ? ' on' : ''}`} onClick={() => setDay('')}>
          senza data
        </button>
      </div>
      <div className="when">
        <label>
          Ora <span>facoltativa</span>
          <input type="time" value={time} onChange={(ev) => setTime(ev.target.value)} />
        </label>
        <button className="btn primary" disabled={busy || unchanged}
                onClick={async () => {
                  setBusy(true);
                  try {
                    // time_note is deliberately not touched: "Mattina" is still
                    // true on Thursday, and it is the only timing left on an
                    // entry moved to a day with no clock.
                    await onMove({
                      event_date: day || null,
                      starts_at: isoInZone(day, time, eventZone),
                      timezone: eventZone,
                    });
                    onDone();
                  } catch (err) { alert(err.message); }
                  finally { setBusy(false); }
                }}>
          {busy ? 'Sposto…' : 'Sposta'}
        </button>
      </div>
      {day === '' && (
        <p className="hint">Senza data finisce in «Da collocare», non su un giorno.</p>
      )}
      {/* Said out loud because the other way of changing your mind, «Cambia»,
          does throw the booking away — and from here the two look alike. */}
      {event.documents?.length > 0 && (
        <p className="hint">
          {event.documents.length === 1
            ? 'Il documento resta allegato alla prenotazione.'
            : 'I documenti restano allegati alla prenotazione.'}
        </p>
      )}
    </div>
  );
}
